import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Container,
  Fab,
  useScrollTrigger,
  Divider,
  MenuItem,
  Menu,
  Collapse,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContactPhoneOutlinedIcon from "@mui/icons-material/ContactPhoneOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Link, useLocation, useNavigate } from "react-router-dom";
// import logo from "../../assets/hero-img.webp";
import logo from "../../assets/nds-logo.png";
import { CLINIC_INFO } from "../../data/siteData";
import { SERVICES_DETAIL } from "../../data/servicesDetail";

const SERVICE_SUB_ITEMS = SERVICES_DETAIL.map(({ id, title }) => ({
  label: title,
  id,
}));

const SERVICE_COLORS = Object.fromEntries(
  SERVICES_DETAIL.map((s) => [s.id, s.color]),
);

const SERVICE_ICONS = Object.fromEntries(
  SERVICES_DETAIL.map((s) => [s.id, s.icon]),
);

// ── Module-level constants — never recreated ──
const NAV_LINKS = [
  { label: "Home", path: "/", icon: HomeOutlinedIcon },
  { label: "About", path: "/about", icon: InfoOutlinedIcon },
  {
    label: "Services",
    path: "/services",
    icon: MedicalServicesOutlinedIcon,
    subItems: SERVICE_SUB_ITEMS,
  },
  { label: "Reviews", path: "/reviews", icon: RateReviewOutlinedIcon },
  { label: "Contact", path: "/contact", icon: ContactPhoneOutlinedIcon },
];

const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

export default function Navbar({ onBookClick }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileSubMenuOpen, setMobileSubMenuOpen] = useState(null);
  const closeTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const showFab = useScrollTrigger({ disableHysteresis: true, threshold: 300 });

  // Throttled scroll listener
  useEffect(() => {
    let ticking = false;
    const handler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  // ── Stable callbacks — no unstable deps ──
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);

  // const handleMenuOpen = useCallback((event, label) => {
  //   setAnchorEl(event.currentTarget);
  //   setActiveMenu(label);
  // }, []);

  const handleMenuOpen = useCallback((event, label) => {
  if (closeTimer.current) {
    clearTimeout(closeTimer.current);
  }

  setAnchorEl(event.currentTarget);
  setActiveMenu(label);
}, []);

  // Memoized and stable — no other callbacks as deps
  // const handleMenuClose = useCallback(() => {
  //   setAnchorEl(null);
  //   setActiveMenu(null);
  // }, []);
  const handleMenuClose = useCallback(() => {
  closeTimer.current = setTimeout(() => {
    setAnchorEl(null);
    setActiveMenu(null);
  }, 150);
}, []);

  const handleMobileSubMenuClick = useCallback((path) => {
    setMobileSubMenuOpen((prev) => (prev === path ? null : path));
  }, []);

  // FIX: only depends on stable `navigate` — not on handleMenuClose
  const handleServiceClick = useCallback(
    (serviceId) => {
      setDrawerOpen(false);
      setAnchorEl(null); // close desktop menu directly — no fn dep
      setActiveMenu(null); // close desktop menu directly — no fn dep
      setMobileSubMenuOpen(null);
      navigate("/services", { state: { activeServiceId: serviceId } });
    },
    [navigate],
  ); // ← only navigate, which is stable

  return (
    <>
      <AppBar
        position="fixed"
        elevation={scrolled ? 4 : 0}
        sx={{
          background: scrolled
            ? "rgba(255,255,255,0.97)"
            : "rgba(255,255,255,0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: scrolled ? "none" : "1px solid rgba(21,101,192,0.08)",
          transition: "box-shadow 0.3s ease, border-color 0.3s ease",
        }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 60, sm: 64, md: 72 },
              gap: { xs: 1, md: 1.5, lg: 2 },
            }}>
            {/* ── Logo ── */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                overflow: "hidden",
                textDecoration: "none",
                flex: { xs: "1 1 auto", md: "0 0 auto" },
                mr: { xs: 1, md: 1, lg: 3 },
              }}>
              <Box
                component="img"
                src={logo}
                alt="Noble Dental Surgery"
                width={56}
                height={56}
                loading="eager"
                decoding="async"
                sx={{
                  height: { xs: 42, sm: 46, md: 48, lg: 56 },
                  width: "auto",
                  mr: { xs: 1, sm: 1.25 },
                  flexShrink: 0,
                  filter: "drop-shadow(0 2px 4px rgba(21,101,192,0.2))",
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    color: "primary.main",
                    fontWeight: 700,
                    lineHeight: 1.1,
                    fontSize: {
                      xs: "0.92rem",
                      sm: "1rem",
                      md: "0.95rem",
                      lg: "1.15rem",
                    },
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                  Noble Dental Surgery
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "secondary.main",
                    fontWeight: 600,
                    letterSpacing: { xs: 1, md: 0.8, lg: 1 },
                    fontSize: { xs: "0.68rem", sm: "0.75rem" },
                    lineHeight: 1.1,
                    display: { xs: "none", sm: "block" },
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                  Multi-Speciality Dental Clinic & Implant Centre
                </Typography>
              </Box>
            </Box>

            {/* ── Desktop Nav ── */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                gap: { md: 0.15, lg: 0.5 },
                flexGrow: 1,
                minWidth: 0,
                justifyContent: "center",
              }}>
              {NAV_LINKS.map(({ label, path, subItems }) => {
                const isActive = location.pathname === path;
                const menuOpen = activeMenu === label;

                return (
                  <React.Fragment key={path}>
                    <Button
                      component={!subItems ? Link : "button"}
                      to={!subItems ? path : undefined}
                      onMouseEnter={
                        subItems ? (e) => handleMenuOpen(e, label) : undefined
                      }
                      aria-haspopup={subItems ? "true" : undefined}
                      aria-expanded={subItems ? String(menuOpen) : undefined}
                      endIcon={
                        subItems ? (
                          <KeyboardArrowDownIcon
                            sx={{
                              fontSize: "1.1rem !important",
                              transition: "transform 0.2s ease",
                              transform: menuOpen
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                            }}
                          />
                        ) : null
                      }
                      sx={{
                        color: isActive ? "primary.main" : "text.primary",
                        fontWeight: isActive ? 700 : 500,
                        position: "relative",
                        minWidth: 0,
                        px: { md: 1.15, lg: 2 },
                        fontSize: { md: "0.9rem", lg: "0.95rem" },
                        whiteSpace: "nowrap",
                        bgcolor: menuOpen
                          ? "rgba(21,101,192,0.06)"
                          : "transparent",
                        borderRadius: 2,
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: 4,
                          left: "50%",
                          transform: isActive
                            ? "translateX(-50%)"
                            : "translateX(-50%) scaleX(0)",
                          width: "60%",
                          height: 2,
                          background:
                            "linear-gradient(90deg, #1565C0, #00C9A7)",
                          borderRadius: 1,
                          transition: "transform 0.25s ease",
                        },
                        "&:hover::after": {
                          transform: "translateX(-50%) scaleX(1)",
                        },
                      }}>
                      {label}
                    </Button>

                    {/* Desktop submenu */}
                    {subItems && (
                      <Menu
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        disableRestoreFocus
                        slotProps={{
                          paper: {
                            elevation: 0,
                            sx: {
                              mt: 1.2,
                              borderRadius: 0.5,
                              minWidth: 250,
                              py: 1,
                              bgcolor: "#fff",
                              border: "1px solid rgba(21,101,192,0.12)",
                              boxShadow:
                                "0 8px 40px rgba(21,101,192,0.14), 0 2px 8px rgba(0,0,0,0.06)",
                              "&::before": {
                                content: '""',
                                display: "block",
                                position: "absolute",
                                top: 0,
                                left: 16,
                                right: 16,
                                height: 2,
                                borderRadius: "0 0 2px 2px",
                                background:
                                  "linear-gradient(90deg, #1565C0, #00C9A7)",
                              },
                              overflow: "visible",
                            },
                          },
                          list: {
                            onMouseEnter: () =>
                              clearTimeout(closeTimer.current),
                            onMouseLeave: handleMenuClose,
                          },
                        }}>
                        {subItems.map((item) => {
                          const color = SERVICE_COLORS[item.id] || "#1565C0";
                          const icon = SERVICE_ICONS[item.id] || "🦷";
                          return (
                            <MenuItem
                              key={item.id}
                              onClick={() => handleServiceClick(item.id)}
                              sx={{
                                py: 0.5,
                                px: 0.5,
                                mx: 0.5,
                                borderRadius: 1,
                                gap: 0.5,
                                "&:hover": {
                                  bgcolor: `${color}0D`,
                                  "& .menu-item-label": { color },
                                  "& .menu-item-icon-box": {
                                    bgcolor: `${color}20`,
                                  },
                                },
                                "&:active": { bgcolor: `${color}18` },
                              }}>
                              <Box
                                className="menu-item-icon-box"
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 2,
                                  bgcolor: `${color}12`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "1rem",
                                  flexShrink: 0,
                                }}>
                                {icon}
                              </Box>
                              <ListItemText
                                primary={item.label}
                                slotProps={{
                                  primary: {
                                    className: "menu-item-label",
                                    sx: {
                                      fontFamily:
                                        '"DM Sans", Arial, sans-serif',
                                      fontWeight: 400,
                                      fontSize: "0.875rem",
                                      color: "text.primary",
                                    },
                                  },
                                }}
                              />
                              <ChevronRightIcon
                                sx={{
                                  fontSize: 16,
                                  color,
                                  opacity: 0,
                                  flexShrink: 0,
                                  ".MuiMenuItem-root:hover &": { opacity: 1 },
                                }}
                              />
                            </MenuItem>
                          );
                        })}

                        <Divider
                          sx={{ borderColor: "rgba(21,101,192,0.08)", mt: 0.5 }}
                        />
                        <Box sx={{ px: 2.5, py: 1.5 }}>
                          <Box
                            component={Link}
                            to="/services"
                            onClick={handleMenuClose}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                              py: 0.8,
                              borderRadius: 2,
                              bgcolor: "rgba(21,101,192,0.05)",
                              border: "1px solid rgba(21,101,192,0.12)",
                              textDecoration: "none",
                              "&:hover": {
                                bgcolor: "rgba(21,101,192,0.1)",
                                borderColor: "rgba(21,101,192,0.25)",
                              },
                            }}>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                color: "primary.main",
                                fontFamily: '"DM Sans", sans-serif',
                                letterSpacing: "0.04em",
                                fontSize: "0.75rem",
                              }}>
                              VIEW ALL SERVICES
                            </Typography>
                            <ChevronRightIcon
                              sx={{ fontSize: 14, color: "primary.main" }}
                            />
                          </Box>
                        </Box>
                      </Menu>
                    )}
                  </React.Fragment>
                );
              })}
            </Box>

            {/* ── Desktop right ── */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                flexShrink: 0,
                gap: { md: 0.75, lg: 1.5 },
              }}>
              <Button
                href={`tel:${CLINIC_INFO.phone}`}
                startIcon={<PhoneIcon />}
                size="small"
                aria-label={`Call ${CLINIC_INFO.phone}`}
                sx={{
                  color: "secondary.main",
                  fontWeight: 700,
                  minWidth: { md: 42, lg: "auto" },
                  px: { md: 1, lg: 1.5 },
                  whiteSpace: "nowrap",
                  "& .MuiButton-startIcon": { mr: { md: 0, lg: 0.8 } },
                }}>
                <Box
                  component="span"
                  sx={{ display: { md: "none", lg: "inline" } }}>
                  {CLINIC_INFO.phone}
                </Box>
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={onBookClick}
                sx={{
                  minHeight: { md: 42, lg: 44 },
                  minWidth: { md: 116, lg: 154 },
                  px: { md: 2, lg: 2.75 },
                  borderRadius: 999,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  fontWeight: 700,
                }}>
                <Box
                  component="span"
                  sx={{ display: { md: "inline", lg: "none" } }}>
                  Book Now
                </Box>
                <Box
                  component="span"
                  sx={{ display: { md: "none", lg: "inline" } }}>
                  Book Appointment
                </Box>
              </Button>
            </Box>

            {/* ── Mobile right ── */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                ml: "auto",
                alignItems: "center",
                gap: 0.75,
              }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={onBookClick}
                sx={{
                  minHeight: 36,
                  fontSize: "0.75rem",
                  px: 1.5,
                  borderRadius: 999,
                  display: { xs: "none", sm: "flex" },
                }}>
                Book Now
              </Button>
              <IconButton
                onClick={openDrawer}
                color="primary"
                aria-label="Open navigation menu"
                sx={{
                  width: 42,
                  height: 42,
                  border: "1px solid rgba(21,101,192,0.14)",
                  backgroundColor: "rgba(21,101,192,0.06)",
                  "&:hover": { backgroundColor: "rgba(21,101,192,0.12)" },
                }}>
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* ── Mobile Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        keepMounted={false}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "min(86vw, 320px)", sm: 320 },
              maxWidth: "100%",
              overflow: "hidden",
              overflowX: "hidden",
              boxSizing: "border-box",
            },
          },
        }}>
        {/* Drawer header */}
        <Box
          sx={{
            px: 2,
            py: 1.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              minWidth: 0,
            }}>
            <Box
              component="img"
              src={logo}
              alt="Logo"
              width={44}
              height={44}
              sx={{ flexShrink: 0 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color="primary.main"
                noWrap>
                Noble Dental Surgery
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={closeDrawer}
            aria-label="Close navigation menu"
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              border: "1px solid rgba(15,23,42,0.1)",
              "&:hover": { backgroundColor: "rgba(15,23,42,0.06)" },
            }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Nav list */}
        <List
          sx={{
            px: 1,
            py: 1,
            width: "100%",
            overflowX: "hidden",
            boxSizing: "border-box",
          }}>
          {NAV_LINKS.map(({ label, path, icon: Icon, subItems }) => {
            const isActive = location.pathname === path;
            const isOpen = mobileSubMenuOpen === path;

            return (
              <Box
                key={path}
                sx={{
                  width: "100%",
                  overflow: "hidden",
                  boxSizing: "border-box",
                }}>
                <ListItemButton
                  onClick={() => {
                    if (subItems?.length) {
                      handleMobileSubMenuClick(path);
                    } else {
                      closeDrawer();
                      navigate(path);
                    }
                  }}
                  selected={isActive}
                  sx={{
                    minHeight: 48,
                    borderRadius: 2,
                    px: 1.75,
                    my: 0.25,
                    // width constrained — never pushes drawer wider
                    width: "100%",
                    overflow: "hidden",
                    color: isActive ? "primary.main" : "text.primary",
                    "&.Mui-selected": {
                      bgcolor: "rgba(21,101,192,0.1)",
                      "&:hover": { bgcolor: "rgba(21,101,192,0.15)" },
                    },
                    "&:hover": { bgcolor: "rgba(21,101,192,0.07)" },
                  }}>
                  <Icon
                    sx={{
                      mr: 1.5,
                      fontSize: 20,
                      flexShrink: 0,
                      color: isActive ? "primary.main" : "text.secondary",
                    }}
                  />
                  <ListItemText
                    primary={label}
                    slotProps={{
                      primary: {
                        sx: {
                          fontWeight: isActive ? 700 : 500,
                          fontSize: "0.95rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      },
                    }}
                  />
                  {subItems?.length > 0 &&
                    (isOpen ? (
                      <ExpandLess
                        sx={{
                          fontSize: 20,
                          color: "text.secondary",
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <ExpandMore
                        sx={{
                          fontSize: 20,
                          color: "text.secondary",
                          flexShrink: 0,
                        }}
                      />
                    ))}
                </ListItemButton>

                {subItems?.length > 0 && (
                  <Collapse
                    in={isOpen}
                    timeout="auto"
                    unmountOnExit
                    sx={{ width: "100%", overflow: "hidden" }}>
                    <Box
                      sx={{
                        px: 1,
                        mb: 0.5,
                        overflow: "hidden",
                        bgcolor: "rgba(248,255,254,0.8)",
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                      }}>
                      {subItems.map(({ label, id }, idx) => {
                        const color = SERVICE_COLORS[id] || "#1565C0";
                        const icon = SERVICE_ICONS[id] || "🦷";

                        return (
                          <React.Fragment key={id}>
                            <Box
                              component="button"
                              onClick={() => handleServiceClick(id)}
                              sx={{
                                all: "unset",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.2,
                                width: "100%",
                                boxSizing: "border-box",
                                overflow: "hidden",
                                px: 1.5,
                                py: 1,
                                cursor: "pointer",
                                position: "relative",
                                "&::before": {
                                  content: '""',
                                  position: "absolute",
                                  left: 0,
                                  top: "15%",
                                  bottom: "15%",
                                  width: 3,
                                  borderRadius: 2,
                                  background: `linear-gradient(to bottom, ${color}, #00C9A7)`,
                                  opacity: 0,
                                },
                                "&:hover": {
                                  bgcolor: `${color}0D`,
                                  "&::before": { opacity: 1 },
                                  "& .sub-label": { color },
                                },
                                "&:focus-visible": {
                                  outline: `2px solid ${color}`,
                                  outlineOffset: -2,
                                },
                              }}>
                              {/* Icon */}
                              <Box
                                sx={{
                                  width: 26,
                                  height: 26,
                                  minWidth: 26,
                                  borderRadius: 1.5,
                                  bgcolor: `${color}15`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "0.8rem",
                                  flexShrink: 0,
                                }}>
                                {icon}
                              </Box>

                              {/* Label — minWidth:0 + flex:1 stops width expansion */}
                              <Typography
                                className="sub-label"
                                sx={{
                                  fontFamily: '"DM Sans", Arial, sans-serif',
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  color: "text.primary",
                                  lineHeight: 1.35,
                                  textAlign: "left",
                                  minWidth: 0,
                                  flex: 1,
                                  flexBasis: 0,
                                  overflowWrap: "break-word",
                                  wordBreak: "break-word",
                                  maxWidth: "100%",
                                }}>
                                {label}
                              </Typography>
                            </Box>

                            {idx < subItems.length - 1 && (
                              <Divider
                                sx={{
                                  borderColor: "rgba(21,101,192,0.06)",
                                  mx: 1.5,
                                }}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Box>
                  </Collapse>
                )}
              </Box>
            );
          })}
        </List>

        <Divider />

        {/* Bottom CTAs */}
        <Box sx={{ p: 2, display: "grid", gap: 1.25 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              closeDrawer();
              onBookClick();
            }}
            sx={{ minHeight: 48, borderRadius: 999, fontWeight: 700 }}>
            Book Appointment
          </Button>
          <Button
            variant="outlined"
            fullWidth
            href={`tel:${CLINIC_INFO.phone}`}
            startIcon={<PhoneIcon />}
            sx={{ minHeight: 48, borderRadius: 999, fontWeight: 700 }}>
            Call Now
          </Button>
        </Box>
      </Drawer>

      {/* Scroll to top FAB */}
      {showFab && (
        <Fab
          size="small"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          sx={{
            position: "fixed",
            bottom: 90,
            right: 20,
            zIndex: 999,
            background: "linear-gradient(135deg, #1565C0, #00C9A7)",
            color: "#fff",
            boxShadow: 3,
            "&:hover": { transform: "translateY(-3px)" },
          }}>
          <KeyboardArrowUpIcon />
        </Fab>
      )}
    </>
  );
}
